import { SearchField } from '@/components/Shared/SearchField';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { navigationStudio } from '@/constants/navigationStudio';

interface ApplicationProduct {
  id: string;
  name: string;
  icon?: string | null;
}

interface ApplicationSelectorProps {
  products: ApplicationProduct[];
  selectedProductId: string | null;
  search: string;
  isLoading?: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (productId: string) => void;
}

const iconMap: Record<string, SvgIconComponent> = {
  inventory: Inventory2OutlinedIcon,
  monitoring: MonitorHeartOutlinedIcon,
  compliance: GppGoodOutlinedIcon,
  database: StorageOutlinedIcon,
  identity: PersonOutlineOutlinedIcon,
};

export const ApplicationSelector = memo(function ApplicationSelector({
  products,
  selectedProductId,
  search,
  isLoading = false,
  onSearchChange,
  onSelect,
}: ApplicationSelectorProps) {
  const { t } = useTranslation('entity-portal');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: 1,
        borderColor: navigationStudio.panelBorderColor,
        borderRadius: navigationStudio.panelBorderRadius,
        bgcolor: navigationStudio.panelBackground,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Typography
          variant="navSection"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mb: 1.5,
          }}
        >
          {t('navigationConfig.selectApplication')}
        </Typography>
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder={t('navigationConfig.filterApplications')}
          aria-label={t('navigationConfig.filterApplications')}
        />
      </Box>

      <Divider sx={{ borderColor: navigationStudio.panelBorderColor }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 2.5 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                height={48}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : (
          <List
            disablePadding
            aria-label={t('navigationConfig.selectApplication')}
          >
            {products.map((product) => {
              const Icon =
                iconMap[product.icon ?? 'inventory'] ?? AppsOutlinedIcon;
              const isSelected = product.id === selectedProductId;

              return (
                <ListItemButton
                  key={product.id}
                  selected={isSelected}
                  onClick={() => onSelect(product.id)}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: navigationStudio.panelBorderColor,
                    '&.Mui-selected': {
                      bgcolor: navigationStudio.selectedApplicationBg,
                      '&:hover': {
                        bgcolor: navigationStudio.selectedApplicationHoverBg,
                      },
                    },
                    '&:hover': {
                      bgcolor: navigationStudio.nodeHoverBg,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon
                      sx={{
                        fontSize: 20,
                        color: isSelected ? 'primary.main' : 'text.secondary',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(product.name, { defaultValue: product.name })}
                    primaryTypographyProps={{
                      variant: isSelected ? 'subtitle1' : 'subtitle2',
                      color: isSelected ? 'text.primary' : 'text.secondary',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
});
